/**
 * Operations Review Page
 * POST-P0: Manager view for patrols with outstanding stops that need review
 * Enhanced with priority, filters, and SLA tracking
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle2, Clock, MapPin, User,
  RefreshCw, Loader2, ChevronRight, Eye, FileCheck,
  AlertCircle, Filter, ArrowUp, ArrowDown
} from 'lucide-react';

export default function OperationsReviewPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [summary, setSummary] = useState({ pending_review: 0, high_priority: 0, overdue: 0 });
  const [selectedRun, setSelectedRun] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  
  // POST-P0: Filters
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');

  const fetchPendingReview = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      
      const response = await api.get(`/route-runs/pending-review?${params.toString()}`);
      setRuns(response.data.runs || []);
      setSummary(response.data.summary || { pending_review: 0, high_priority: 0, overdue: 0 });
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      toast.error('Failed to load pending reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingReview();
  }, [priorityFilter, statusFilter]);

  const handleMarkReviewed = async (runId) => {
    setReviewing(true);
    try {
      await api.post(`/route-runs/${runId}/review`);
      toast.success('Patrol marked as reviewed');
      setSelectedRun(null);
      fetchPendingReview();
    } catch (error) {
      console.error('Error marking reviewed:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as reviewed');
    } finally {
      setReviewing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'medium': return 'bg-amber-500/20 text-amber-400 border-amber-500/50';
      case 'low': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return <ArrowUp className="w-3 h-3" />;
      case 'medium': return <AlertCircle className="w-3 h-3" />;
      case 'low': return <ArrowDown className="w-3 h-3" />;
      default: return null;
    }
  };

  if (loading && runs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operations-review-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white flex items-center gap-2">
            <FileCheck className="w-6 h-6 text-amber-400" />
            Operations Review
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Patrols closed with outstanding stops requiring management review
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchPendingReview}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-2xl font-bold text-white">{summary.pending_review}</p>
                <p className="text-xs text-zinc-500">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <ArrowUp className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-2xl font-bold text-white">{summary.high_priority}</p>
                <p className="text-xs text-zinc-500">High Priority</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-2xl font-bold text-white">{summary.overdue}</p>
                <p className="text-xs text-zinc-500">Overdue (&gt;24h)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              <div>
                <p className="text-2xl font-bold text-white">
                  {runs.filter(r => r.compliance_status === 'compliant').length}
                </p>
                <p className="text-xs text-zinc-500">Compliant</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-500" />
          <span className="text-sm text-zinc-500">Filters:</span>
        </div>
        
        {/* Priority Filter */}
        <div className="flex gap-1">
          {['all', 'high', 'medium', 'low'].map((p) => (
            <Button
              key={p}
              variant={priorityFilter === p ? 'default' : 'outline'}
              size="sm"
              className={`text-xs ${priorityFilter === p ? '' : 'border-zinc-700'}`}
              onClick={() => setPriorityFilter(p)}
            >
              {p === 'all' ? 'All Priority' : `${p.charAt(0).toUpperCase() + p.slice(1)}`}
            </Button>
          ))}
        </div>
        
        <div className="h-4 w-px bg-zinc-700" />
        
        {/* Status Filter */}
        <div className="flex gap-1">
          {['pending', 'reviewed', 'overdue', 'all'].map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              className={`text-xs ${statusFilter === s ? '' : 'border-zinc-700'}`}
              onClick={() => setStatusFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Patrols List */}
      {runs.length === 0 ? (
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <p className="text-zinc-400">No patrols match your filters</p>
            <p className="text-xs text-zinc-500 mt-2">Try adjusting your filter criteria</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {runs.map((run) => (
            <Card
              key={run.id}
              className={`bg-[#18181b] border-[#3f3f46] cursor-pointer transition-all hover:border-zinc-600 ${
                selectedRun?.id === run.id ? 'border-amber-500' : ''
              } ${run.is_overdue ? 'border-l-4 border-l-red-500' : ''}`}
              onClick={() => setSelectedRun(selectedRun?.id === run.id ? null : run)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Priority Badge */}
                    <Badge className={`${getPriorityColor(run.priority)} flex items-center gap-1`}>
                      {getPriorityIcon(run.priority)}
                      {run.priority?.toUpperCase()}
                    </Badge>
                    
                    <div>
                      <h3 className="font-medium text-white flex items-center gap-2">
                        {run.route_name}
                        {run.is_overdue && (
                          <Badge className="bg-red-500/20 text-red-400 text-xs">
                            OVERDUE
                          </Badge>
                        )}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {run.site_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {run.officer_details?.name || run.guard_name || 'Unknown'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(run.end_time)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <Badge className={`${
                        run.compliance_status === 'compliant'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {run.completion_percentage || 0}% Complete
                      </Badge>
                      {run.summary?.pending > 0 && (
                        <p className="text-xs text-amber-400 mt-1">
                          {run.summary.pending} stop{run.summary.pending !== 1 ? 's' : ''} outstanding
                        </p>
                      )}
                      {run.review_due_at && !run.is_overdue && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Due: {formatDate(run.review_due_at)}
                        </p>
                      )}
                    </div>
                    <ChevronRight className={`w-5 h-5 text-zinc-500 transition-transform ${
                      selectedRun?.id === run.id ? 'rotate-90' : ''
                    }`} />
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedRun?.id === run.id && (
                  <div className="mt-4 pt-4 border-t border-[#3f3f46]">
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Summary</p>
                        <div className="space-y-1 text-sm">
                          <p className="text-zinc-300">
                            Completed: {run.summary?.completed || 0} / {run.summary?.total_stops || 0} stops
                          </p>
                          {run.summary?.pending > 0 && (
                            <p className="text-amber-400">
                              Outstanding: {run.summary.pending} stops
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Patrol Duration</p>
                        <p className="text-sm text-zinc-300">
                          {run.start_time && run.end_time
                            ? `${Math.round((new Date(run.end_time) - new Date(run.start_time)) / 60000)} mins`
                            : '-'
                          }
                        </p>
                        {run.distance_km && (
                          <p className="text-xs text-zinc-500">{run.distance_km} km traveled</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">SLA Status</p>
                        {run.is_overdue ? (
                          <p className="text-sm text-red-400 font-medium">Review Overdue</p>
                        ) : run.review_due_at ? (
                          <p className="text-sm text-zinc-300">Due: {formatDate(run.review_due_at)}</p>
                        ) : (
                          <p className="text-sm text-emerald-400">Compliant</p>
                        )}
                      </div>
                    </div>

                    {/* Outstanding Stops List */}
                    {run.stops?.filter(s => s.status === 'pending' || !s.status).length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-zinc-500 mb-2">Outstanding Stops</p>
                        <div className="space-y-1">
                          {run.stops
                            .filter(s => s.status === 'pending' || !s.status)
                            .map((stop, idx) => (
                              <div
                                key={stop.id || idx}
                                className="flex items-center gap-2 p-2 bg-[#09090b] rounded text-sm"
                              >
                                <AlertTriangle className="w-4 h-4 text-amber-400" />
                                <span className="text-zinc-300">
                                  {stop.checkpoint_name || stop.sector_name || stop.name || `Stop ${idx + 1}`}
                                </span>
                                {stop.intentionally_skipped && (
                                  <Badge variant="outline" className="text-xs border-zinc-600">
                                    Marked Skip
                                  </Badge>
                                )}
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )}

                    {/* Review Actions */}
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Could navigate to full patrol details
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Full Details
                      </Button>
                      {run.review_status !== 'reviewed' && (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkReviewed(run.id);
                          }}
                          disabled={reviewing}
                        >
                          {reviewing ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                          )}
                          Mark Reviewed
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
